import { useQuery } from '@tanstack/react-query';
import { CalendarClock, CalendarDays, CalendarX2, GraduationCap } from 'lucide-react';
import { instructorApi } from '../../api/instructor';
import { useAuthStore } from '../../store/auth';
import { useUiStore } from '../../store/ui';
import { fmtDayShort, fmtTime } from '../../lib/dates';
import { NEXT_EVENT_WEEKS, type NextEvent } from '../../lib/nextEvent';
import { Button, Card } from '../charte';

// Ma journée sans leçon à venir aujourd'hui (J28, 28/10/2026) : le prochain événement daté, leçon ou accompagnement à
// l'examen, au lieu de « Aucune leçon prévue aujourd'hui ».

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Jour et heure d'un accompagnement, dans le fuseau de son centre d'examen. */
function examWhen(event: Extract<NextEvent, { kind: 'exam' }>) {
  const timeZone = event.accompaniment.timezone || 'Europe/Paris';
  const at = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { timeZone, hour: '2-digit', minute: '2-digit' });
  const day = new Date(event.startsAt).toLocaleDateString('fr-FR', { timeZone, weekday: 'long', day: 'numeric', month: 'long' });
  return `${capitalized(day)} · ${at(event.startsAt)} – ${at(event.endsAt)}`;
}

function describe(event: NextEvent) {
  if (event.kind === 'exam')
    return {
      icon: GraduationCap,
      eyebrow: 'Prochain accompagnement à l’examen',
      when: examWhen(event),
      what: [event.accompaniment.candidate_name, event.accompaniment.center].filter(Boolean).join(' · '),
    };
  const { lesson } = event;
  const name = lesson.candidate ? `${lesson.candidate.firstName} ${lesson.candidate.lastName}` : null;
  return {
    icon: CalendarClock,
    eyebrow: 'Prochaine leçon',
    when: `${capitalized(fmtDayShort(event.startsAt))} · ${fmtTime(event.startsAt)} – ${fmtTime(event.endsAt)}`,
    what: [name, lesson.lessonType ?? 'Conduite'].filter(Boolean).join(' · '),
  };
}

export function NextEventCard() {
  const userId = useAuthStore((s) => s.user?.id);
  const setSection = useUiStore((s) => s.setSection);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['next-event', userId],
    queryFn: () => instructorApi.getNextEvent(),
    staleTime: 5 * 60_000,
  });

  const shown = data ? describe(data) : null;
  const Icon = shown?.icon ?? CalendarX2;
  return (
    <Card className="flex flex-wrap items-center gap-4 p-5 sm:p-6" aria-live="polite">
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-surface-indigo text-primary">
        <Icon size={22} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <p className="text-sm text-secondary">Recherche de votre prochaine leçon…</p>
        ) : shown ? (
          <>
            <p className="text-eyebrow uppercase text-primary-600">{shown.eyebrow}</p>
            <p className="text-[15px] font-semibold text-heading">{shown.when}</p>
            {shown.what && <p className="text-sm text-secondary">{shown.what}</p>}
          </>
        ) : (
          <>
            <p className="text-[15px] font-semibold text-heading">Aucune leçon ni accompagnement à venir</p>
            <p className="text-sm text-secondary">
              {isError ? 'Votre semaine est dans « Planning ».' : `Rien n’est prévu dans les ${NEXT_EVENT_WEEKS} prochaines semaines.`}
            </p>
          </>
        )}
      </div>
      <Button size="sm" icon={CalendarDays} onClick={() => setSection('planning')}>
        Voir le planning
      </Button>
    </Card>
  );
}
